import {
  format,
  addDays,
  isAfter,
  isBefore,
  differenceInSeconds,
  endOfDay,
} from "date-fns";
import { getUnifiedDeliveryData } from "../lib/unifiedDeliveryService"; // Add this import
import { DateService } from "./dateUtils"; // Add DateService import

interface DaysOpen {
  monday: boolean;
  mondayOpeningTime: string;
  mondayClosingTime: string;
  tuesday: boolean;
  tuesdayOpeningTime: string;
  tuesdayClosingTime: string;
  wednesday: boolean;
  wednesdayOpeningTime: string;
  wednesdayClosingTime: string;
  thursday: boolean;
  thursdayOpeningTime: string;
  thursdayClosingTime: string;
  friday: boolean;
  fridayOpeningTime: string;
  fridayClosingTime: string;
  saturday: boolean;
  saturdayOpeningTime: string;
  saturdayClosingTime: string;
  sunday: boolean;
  sundayOpeningTime: string;
  sundayClosingTime: string;
}

interface DeliveryOptions {
  expressDelivery: boolean;
  limitTime: string;
  daysToDeliver: number;
  daysOpen: DaysOpen;
  opsLeadTime: {
    opsLeadTime: number;
    originZone: string;
    destinationZone: string;
  };
}

const parseTime = (time: string, currentDate: Date): Date | null => {
  const [hours, minutes] = time.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    console.warn(`Invalid time format: ${time}`);
    return null;
  }
  const dateTime = new Date(currentDate);
  dateTime.setHours(hours, minutes, 0, 0);
  return dateTime;
};

const getOpeningClosingTimes = (
  date: Date,
  daysOpen: DaysOpen
): { openingTime: string; closingTime: string } | null => {
  const dayOfWeek = format(date, "EEEE").toLowerCase();
  const isOpen = daysOpen[`${dayOfWeek}` as keyof DaysOpen] as boolean;
  if (!isOpen) return null;
  const openingTime = daysOpen[
    `${dayOfWeek}OpeningTime` as keyof DaysOpen
  ] as string;
  const closingTime = daysOpen[
    `${dayOfWeek}ClosingTime` as keyof DaysOpen
  ] as string;
  return { openingTime, closingTime };
};

const isOpenOnDay = (date: Date, daysOpen: DaysOpen): boolean => {
  const dayOfWeek = format(date, "EEEE").toLowerCase();
  if (!(dayOfWeek in daysOpen)) {
    console.error(`Day of week ${dayOfWeek} is not a valid key in daysOpen`);
    return false;
  }
  const isOpen = daysOpen[`${dayOfWeek}` as keyof DaysOpen] as boolean;
  return isOpen || false;
};

const validateFields = (options: DeliveryOptions): string | null => {
  if (options.expressDelivery === undefined) {
    console.error("Missing field: expressDelivery");
    return "expressDelivery";
  }
  if (!options.limitTime) {
    console.error("Missing field: limitTime");
    return "limitTime";
  }
  if (options.daysToDeliver === undefined) {
    console.error("Missing field: daysToDeliver");
    return "daysToDeliver";
  }
  if (options.opsLeadTime === undefined) {
    console.error("Missing field: opsLeadTime");
    return "opsLeadTime";
  }
  if (!options.daysOpen) {
    console.error("Missing field: daysOpen");
    return "daysOpen";
  }
  return null;
};

const calculateRapidoDelivery = (
  options: DeliveryOptions,
  currentDate: Date
): { isRapidoAvailable: boolean; rapidoAvailableDates: Date[] } => {
  const { daysOpen, daysToDeliver } = options;
  const rapidoAvailableDates: Date[] = [];

  const getOrderReceivedDay = (startDate: Date): Date => {
    while (!isOpenOnDay(startDate, daysOpen)) {
      startDate = addDays(startDate, 1);
    }
    return startDate;
  };

  for (let i = 1; i <= 2; i++) {
    const potentialDate = addDays(currentDate, i);
    let deliveryDate = getOrderReceivedDay(potentialDate);
    for (let j = 0; j < daysToDeliver; j++) {
      deliveryDate = addDays(deliveryDate, 1);
      if (!isOpenOnDay(deliveryDate, daysOpen)) {
        deliveryDate = getOrderReceivedDay(addDays(deliveryDate, 1));
      }
    }
    if (deliveryDate.getTime() === potentialDate.getTime()) {
      rapidoAvailableDates.push(potentialDate);
    }
  }

  return {
    isRapidoAvailable: rapidoAvailableDates.length > 0,
    rapidoAvailableDates,
  };
};

const calculateStandardDelivery = (
  options: DeliveryOptions,
  currentDate: Date,
  rapidoAvailableDates: Date[]
): Date => {
  const { daysToDeliver, opsLeadTime, daysOpen } = options;

  const MAX_ITERATIONS = 365;

  const getOrderReceivedDay = (startDate: Date): Date => {
    while (!isOpenOnDay(startDate, daysOpen)) {
      startDate = addDays(startDate, 1);
    }
    return startDate;
  };

  let startDate = getOrderReceivedDay(currentDate);
  let iterations = 0;

  console.log("Starting date for delivery calculation:", startDate);

  let daysCounted = 0;
  while (daysCounted < daysToDeliver) {
    startDate = addDays(startDate, 1);
    if (isOpenOnDay(startDate, daysOpen)) {
      daysCounted++;
      console.log(`Day ${daysCounted} counted:`, startDate);
    }
    iterations++;
    if (iterations > MAX_ITERATIONS) {
      throw new Error("Exceeded maximum iterations in daysToDeliver");
    }
  }

  iterations = 0;
  for (let i = 0; i < opsLeadTime.opsLeadTime; i++) {
    startDate = addDays(startDate, 1);
    while (!isOpenOnDay(startDate, daysOpen)) {
      startDate = addDays(startDate, 1);
      iterations++;
      if (iterations > MAX_ITERATIONS) {
        throw new Error("Exceeded maximum iterations in opsLeadTime");
      }
    }
    console.log(`Ops lead time day ${i + 1} counted:`, startDate);
  }

  const latestRapidoDate = rapidoAvailableDates.length
    ? rapidoAvailableDates[rapidoAvailableDates.length - 1]
    : null;

  if (latestRapidoDate) {
    startDate = addDays(latestRapidoDate, 1);
    iterations = 0;

    while (!isOpenOnDay(startDate, daysOpen)) {
      startDate = addDays(startDate, 1);
      iterations++;
      if (iterations > MAX_ITERATIONS) {
        throw new Error("Exceeded maximum iterations in open day check");
      }
    }

    console.log("Modified standard date to be after rapido:", startDate);
  }

  console.log("Standard Delivery Date:", startDate);

  const normalizedDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
    12,
    0,
    0,
    0
  );

  return normalizedDate;
};

const isExpressAvailable = (
  options: DeliveryOptions,
  currentTime: Date,
  availableMethods: { [key: string]: boolean }
): boolean => {
  const missingField = validateFields(options);
  if (missingField || !availableMethods.express) return false;

  const { expressDelivery, limitTime, daysOpen } = options;
  if (!expressDelivery) return false;

  if (!isOpenOnDay(currentTime, daysOpen)) return false;
  const times = getOpeningClosingTimes(currentTime, daysOpen);
  if (!times) return false;

  const limitDateTime = parseTime(limitTime, currentTime);
  const closingDateTime = parseTime(times.closingTime, currentTime);

  if (!limitDateTime || !closingDateTime) return false;

  console.log("Express Delivery Date:", currentTime);

  return (
    isBefore(currentTime, limitDateTime) &&
    isBefore(currentTime, closingDateTime)
  );
};

const getCountdown = (currentDate: Date, limitTime: string): string => {
  if (typeof limitTime !== "string") {
    console.warn("Invalid limitTime value:", limitTime);
    return "Invalid Time";
  }
  const [limitHours, limitMinutes] = limitTime.split(":").map(Number);
  const limitDateTime = new Date(currentDate);
  limitDateTime.setHours(limitHours, limitMinutes, 0, 0);

  const secondsLeft = isAfter(currentDate, limitDateTime)
    ? differenceInSeconds(endOfDay(currentDate), currentDate)
    : differenceInSeconds(limitDateTime, currentDate);

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
};

const getDelivery = (
  options: DeliveryOptions,
  currentDate: Date,
  availableMethods: { [key: string]: boolean }
): {
  express: { isAvailable: boolean; countdown: string | null };
  rapido: {
    isAvailable: boolean;
    availableDates: string[];
    countdown: string | null;
  };
  standard: { startDate: string; countdown: string | null };
} => {
  console.log("getDelivery options:", options);
  const isExpressAvailableFlag =
    availableMethods.express &&
    isExpressAvailable(options, currentDate, availableMethods);
  const expressCountdown = isExpressAvailableFlag
    ? getCountdown(currentDate, options.limitTime)
    : null;

  const rapidoDelivery = availableMethods.rapido
    ? calculateRapidoDelivery(options, currentDate)
    : { isRapidoAvailable: false, rapidoAvailableDates: [] };
  const rapidoCountdown = rapidoDelivery.isRapidoAvailable
    ? getCountdown(currentDate, options.limitTime)
    : null;

  const standardStartDate = availableMethods.standard
    ? calculateStandardDelivery(
        options,
        currentDate,
        rapidoDelivery.rapidoAvailableDates
      )
    : null;
  const standardCountdown = standardStartDate
    ? getCountdown(currentDate, options.limitTime)
    : null;

  return {
    express: {
      isAvailable: isExpressAvailableFlag,
      countdown: expressCountdown,
    },
    rapido: {
      isAvailable: rapidoDelivery.isRapidoAvailable,
      availableDates: rapidoDelivery.rapidoAvailableDates.map(
        (date) => date.toISOString().split("T")[0]
      ),
      countdown: rapidoCountdown,
    },
    standard: {
      startDate: standardStartDate
        ? `${standardStartDate.getFullYear()}-${String(
            standardStartDate.getMonth() + 1
          ).padStart(2, "0")}-${String(standardStartDate.getDate()).padStart(
            2,
            "0"
          )}`
        : "No disponible",
      countdown: standardCountdown,
    },
  };
};

const getDeliveryOptions = (
  item: any,
  opsLeadTime: {
    opsLeadTime: number;
    originZone: string;
    destinationZone: string;
  }
): DeliveryOptions | null => {
  console.log("Getting delivery options for item:", item);
  if (!item || !item.logistics || !item.partner?.daysOpen) {
    console.error("Invalid product logistics or daysOpen data:", item);
    return null;
  }

  if (
    !("expressDelivery" in item.logistics) ||
    !("limitTime" in item.logistics) ||
    !item.partner?.daysOpen
  ) {
    console.error("Incomplete logistics or daysOpen data:", item);
    return null;
  }

  console.log("daysOpen object:", item.partner.daysOpen);

  return {
    expressDelivery: item.logistics.expressDelivery,
    limitTime: item.logistics.limitTime,
    daysToDeliver: item.logistics.minDaysToDeliver
      ? item.logistics.minDaysToDeliver
      : item.logistics.daysToDeliver,
    opsLeadTime: opsLeadTime,
    daysOpen: item.partner.daysOpen,
  };
};

const calculateGroupStandardDeliveryDates = async (
  group: any,
  zoneIds: { deliveryLeadTimeZoneId?: string; deliveryFeeZoneId?: string },
  preCalculatedLeadTimes?: any[]
) => {
  if (!group || !group.deliveryFeeZone?.documentId) {
    console.log("Missing required group data for delivery date calculation");
    return new Date();
  }

  if (!zoneIds.deliveryLeadTimeZoneId || !zoneIds.deliveryFeeZoneId) {
    console.log("Missing destination zone IDs for delivery date calculation");
    return new Date();
  }

  try {
    console.log(`Calculating group delivery dates for group ID: ${group.id}`);

    const products = await Promise.all(
      group.products.map(async (product: any, index: number) => {
        console.log(
          `Calculating delivery date for product ID: ${product.id} in group ID: ${group.id}`
        );

        const originLeadTimeZoneId =
          product.product.partner.originZones.deliveryLeadTimeZone.documentId;
        const originFeeZoneId =
          product.product.partner.originZones.deliveryFeeZone.documentId;
        const destinationLeadTimeZoneId = zoneIds.deliveryLeadTimeZoneId;
        const destinationFeeZoneId = zoneIds.deliveryFeeZoneId;

        if (!originLeadTimeZoneId || !destinationLeadTimeZoneId) {
          console.warn("Missing zone IDs for ops lead time calculation", {
            originLeadTimeZoneId,
            destinationLeadTimeZoneId,
          });
          return product;
        }

        let opsLeadTimeData;
        if (preCalculatedLeadTimes && preCalculatedLeadTimes[index]) {
          console.log("Using pre-calculated ops lead time data");
          opsLeadTimeData = preCalculatedLeadTimes[index];
        } else {
          console.log("Fetching unified delivery data");
          const deliveryData = await getUnifiedDeliveryData({
            originLeadTimeZoneId,
            originFeeZoneId,
            destinationLeadTimeZoneId,
            destinationFeeZoneId: destinationFeeZoneId || "",
          });
          opsLeadTimeData = deliveryData.opsLeadTime;
        }

        console.log("Ops Lead Time Data:", opsLeadTimeData);
        console.log("product", product.product);

        const deliveryOptions = getDeliveryOptions(product.product, {
          opsLeadTime: opsLeadTimeData.opsLeadTime,
          originZone: originLeadTimeZoneId,
          destinationZone: destinationLeadTimeZoneId,
        });

        if (!deliveryOptions) {
          console.error(
            `Invalid delivery options for product ID: ${product.id} in group ID: ${group.id}`
          );
          return product;
        }

        const availableMethods = {
          express: true,
          rapido: true,
          standard: true,
        };

        const delivery = getDelivery(
          deliveryOptions,
          new Date(),
          availableMethods
        );

        return {
          ...product,
          standardDeliveryDate: delivery.standard.startDate,
        };
      })
    );

    console.log(
      `Products with calculated delivery dates for group ID: ${group.id}:`,
      products
    );

    const latestStandardDeliveryDate = products.reduce((latest, product) => {
      const standardDeliveryDate =
        product.standardDeliveryDate || product.deliveryDate;

      let deliveryDate;
      if (typeof standardDeliveryDate === "string") {
        try {
          deliveryDate =
            DateService.createDateFromApiFormat(standardDeliveryDate);
        } catch (error) {
          console.error("Error parsing delivery date:", error);
          deliveryDate = new Date(0);
        }
      } else {
        deliveryDate = new Date(0);
      }

      if (isNaN(deliveryDate.getTime())) {
        return latest;
      }

      const noonDeliveryDate = new Date(
        deliveryDate.getFullYear(),
        deliveryDate.getMonth(),
        deliveryDate.getDate(),
        12,
        0,
        0
      );

      return noonDeliveryDate > latest ? noonDeliveryDate : latest;
    }, new Date(0));

    console.log(
      `Latest standard delivery date for group ID: ${group.id}:`,
      latestStandardDeliveryDate
    );

    return new Date(
      latestStandardDeliveryDate.getFullYear(),
      latestStandardDeliveryDate.getMonth(),
      latestStandardDeliveryDate.getDate(),
      12,
      0,
      0
    );
  } catch (error) {
    console.error("Error calculating delivery dates:", error);
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      12,
      0,
      0
    );
  }
};

export {
  isExpressAvailable,
  getDeliveryOptions,
  calculateRapidoDelivery,
  calculateStandardDelivery,
  getCountdown,
  getDelivery,
  calculateGroupStandardDeliveryDates,
};
